/**
 * Settings API Service (TASK-051)
 *
 * API client for settings management.
 * TDD Stub - will be implemented after tests pass.
 */
import apiClient from './client';
import type {
  Settings,
  OrganizationSettings,
  AISettings,
  IntegrationsSettings,
  SystemSettings,
  UpdateOrganizationPayload,
  UpdateAIPayload,
  UpdateIntegrationsPayload,
  UpdateSystemPayload,
} from '@/types/settings';

/**
 * Settings API service
 */
export const settingsApi = {
  /**
   * Get all settings
   * @returns All settings (organization, AI, integrations, system)
   */
  getSettings: async (): Promise<Settings> => {
    const response = await apiClient.get<Settings>('/api/v1/settings/');
    return response.data;
  },

  /**
   * Update organization settings
   * @param payload - Organization settings to update
   * @returns Updated organization settings
   */
  updateOrganization: async (
    payload: UpdateOrganizationPayload
  ): Promise<OrganizationSettings> => {
    const response = await apiClient.put<OrganizationSettings>(
      '/api/v1/settings/organization/',
      payload
    );
    return response.data;
  },

  /**
   * Update AI settings
   * @param payload - AI settings to update
   * @returns Updated AI settings
   */
  updateAI: async (payload: UpdateAIPayload): Promise<AISettings> => {
    const response = await apiClient.put<AISettings>(
      '/api/v1/settings/ai/',
      payload
    );
    return response.data;
  },

  /**
   * Update integrations settings
   * @param payload - Integrations settings to update
   * @returns Updated integrations settings
   */
  updateIntegrations: async (
    payload: UpdateIntegrationsPayload
  ): Promise<IntegrationsSettings> => {
    const response = await apiClient.put<IntegrationsSettings>(
      '/api/v1/settings/integrations/',
      payload
    );
    return response.data;
  },

  /**
   * Update system settings
   * @param payload - System settings to update
   * @returns Updated system settings
   */
  updateSystem: async (
    payload: UpdateSystemPayload
  ): Promise<SystemSettings> => {
    const response = await apiClient.put<SystemSettings>(
      '/api/v1/settings/system/',
      payload
    );
    return response.data;
  },

  /**
   * Test Telegram bot connection
   * @returns Test result
   */
  testTelegramConnection: async (): Promise<{ success: boolean; message: string }> => {
    const response = await apiClient.post<{ success: boolean; message: string }>(
      '/api/v1/settings/integrations/test-telegram/'
    );
    return response.data;
  },

  /**
   * Regenerate webhook secret
   * @returns New masked webhook secret
   */
  regenerateWebhookSecret: async (): Promise<{ webhook_secret: string }> => {
    const response = await apiClient.post<{ webhook_secret: string }>(
      '/api/v1/settings/integrations/regenerate-webhook-secret/'
    );
    return response.data;
  },

  /**
   * Upload organization logo
   * @param file - Logo file to upload
   * @returns Updated organization settings with new logo URL
   */
  uploadLogo: async (file: File): Promise<OrganizationSettings> => {
    const formData = new FormData();
    formData.append('logo', file);

    const response = await apiClient.post<OrganizationSettings>(
      '/api/v1/settings/organization/logo/',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  },

  /**
   * Delete organization logo
   * @returns Updated organization settings without logo
   */
  deleteLogo: async (): Promise<OrganizationSettings> => {
    const response = await apiClient.delete<OrganizationSettings>(
      '/api/v1/settings/organization/logo/'
    );
    return response.data;
  },
};
